import {DataStatus, TabProps, Tabs} from 'platform/components';

import {useGetServiceCheckInQuery, useGetServiceHandOverQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {queryParams, Section, usePermissions} from '@dms/shared';
import {NewInspectionButton} from '@dms/teas';

import {useQueryState} from 'shared/web';

import {
  CHECKIN_SECTION,
  HANDOVER_SECTION,
  VEHICLE_INSPECTION_SECTION,
} from '../../../../constants/detailSections';
import {useWorkshopUrl} from '../../../../hooks/useWorkshopUrl';
import {CheckIn} from './components/CheckIn';
import {HandOver} from './components/HandOver';
import {InspectionsTab} from './components/InspectionsTab';

interface CheckinHandoverProps {
  vehicleId: string;
  isReadOnly?: boolean;
}

export function CheckinHandover(props: CheckinHandoverProps) {
  const {serviceCaseId: id} = useWorkshopUrl();
  const {
    data: checkin,
    isLoading: isCheckinLoading,
    isError: isCheckinError,
  } = useGetServiceCheckInQuery({serviceCaseId: id});
  const {
    data: handover,
    isLoading: isHandoverLoading,
    isError: isHandoverError,
  } = useGetServiceHandOverQuery({serviceCaseId: id});

  const [activeTabId, setActiveTabId] = useQueryState(queryParams.SERVICE_CASE_CHECKIN_HANDOVER);

  const [hasCreateInspectionPermission] = usePermissions({
    permissionKeys: ['createInspection'],
    scopes: {
      createInspection: {
        inspectionType: 'SKIP',
      },
    },
  });

  const tabs: TabProps[] = [
    {
      id: CHECKIN_SECTION,
      title: i18n.t('entity.workshop.labels.checkIn'),
      'data-testid': testIds.workshop.serviceCaseDetail('checkinTab'),
      content: <CheckIn vehicleId={props.vehicleId} isReadOnly={props.isReadOnly} />,
      icon: checkin?.state === 'COMPLETED' ? 'success' : 'default',
    },
    {
      id: HANDOVER_SECTION,
      title: i18n.t('entity.workshop.labels.handover'),
      'data-testid': testIds.workshop.serviceCaseDetail('handoverTab'),
      content: <HandOver vehicleId={props.vehicleId} isReadOnly={props.isReadOnly} />,
      icon: handover?.state === 'COMPLETED' ? 'success' : 'default',
    },
    {
      id: VEHICLE_INSPECTION_SECTION,
      title: i18n.t('entity.vehicle.labels.inspections'),
      'data-testid': testIds.workshop.serviceCaseDetail('inspectionsTab'),
      content: <InspectionsTab vehicleId={props.vehicleId} />,
      actions:
        hasCreateInspectionPermission && !props.isReadOnly
          ? [
              {
                type: 'custom',
                component: <NewInspectionButton vehicleId={props.vehicleId} openLinkInNewTab />,
              },
            ]
          : [],
    },
  ];

  return (
    <Section data-testid={testIds.workshop.serviceCaseDetail('checkinHandover')}>
      <DataStatus
        isLoading={isCheckinLoading || isHandoverLoading}
        isError={isCheckinError || isHandoverError}
      >
        <Tabs
          variant="condensed"
          activeTabId={activeTabId}
          onChange={setActiveTabId}
          tabs={tabs}
          isLazy
          data-testid={testIds.workshop.serviceCaseDetail('checkinHandover')}
        />
      </DataStatus>
    </Section>
  );
}
